// Customer-protected pages: /account (profile edit) and /orders (history).
// Both require an authenticated customer token. Falls back to /login if
// the user is not authenticated.
import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, LogOut, User, Mail, MessageSquare, Package, ExternalLink, ShieldCheck } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLanguage } from "@/context/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const STR = {
  en: {
    account_title: "My Account",
    orders_title: "Bob Store Orders",
    financial_title: "Dashboard",
    financial_sub: "Odoo service orders, invoices, and account balance.",
    logout: "Log out",
    save: "Save changes",
    saved: "Profile saved",
    name: "Full name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    city: "City",
    country: "Country",
    account_status: "Account status",
    status_active: "Active",
    status_inactive: "Inactive",
    status_frozen: "Frozen",
    status_deleted: "Deleted",
    delivery_notes: "Delivery notes",
    customer_id: "Customer ID",
    change_pwd: "Change password",
    current_pwd: "Current password",
    new_pwd: "New password",
    no_orders: "You haven't placed any orders yet.",
    browse: "Browse the BOB Store",
    order_num: "Order #",
    date: "Date",
    status: "Status",
    total: "Total",
    items: "Items",
    whatsapp_help: "Need help? Message us on WhatsApp",
    legal_support: "Legal & support",
    terms: "Terms",
    privacy: "Privacy",
    prohibited: "Prohibited Items",
    loading: "Loading...",
    profile_section: "Profile details",
    security_section: "Password & access",
    support_note: "Keep your delivery details up to date for smoother order handling and support.",
    orders_note: "Track order history, totals, and status from one place.",
    orders_empty_note: "When you place an order, it will appear here with its status and total.",
  },
  ar: {
    account_title: "حسابي",
    orders_title: "طلبات متجر BOB",
    financial_title: "لوحة التحكم",
    financial_sub: "طلبات الخدمة من Odoo والفواتير ورصيد الحساب.",
    logout: "تسجيل الخروج",
    save: "حفظ التغييرات",
    saved: "تم حفظ الملف الشخصي",
    name: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    address: "العنوان",
    city: "المدينة",
    country: "البلد",
    account_status: "حالة الحساب",
    status_active: "نشط",
    status_inactive: "غير نشط",
    status_frozen: "مجمّد",
    status_deleted: "محذوف",
    delivery_notes: "ملاحظات التوصيل",
    customer_id: "رقم العميل",
    change_pwd: "تغيير كلمة المرور",
    current_pwd: "كلمة المرور الحالية",
    new_pwd: "كلمة المرور الجديدة",
    no_orders: "لم تقم بأي طلب بعد.",
    browse: "تصفّح متجر BOB",
    order_num: "رقم الطلب",
    date: "التاريخ",
    status: "الحالة",
    total: "الإجمالي",
    items: "المنتجات",
    whatsapp_help: "تحتاج مساعدة؟ راسلنا على واتساب",
    legal_support: "القانونية والدعم",
    terms: "الشروط",
    privacy: "الخصوصية",
    prohibited: "الممنوعات",
    loading: "جارٍ التحميل...",
    profile_section: "بيانات الملف الشخصي",
    security_section: "كلمة المرور والوصول",
    support_note: "حدّث تفاصيل التوصيل لتسهيل متابعة الطلبات وخدمة الدعم.",
    orders_note: "تابع سجل الطلبات والحالة والإجمالي من مكان واحد.",
    orders_empty_note: "عند إنشاء أول طلب، سيظهر هنا مع حالته وإجماليه.",
  },
  fr: {
    account_title: "Mon compte",
    orders_title: "Commandes Bob Store",
    financial_title: "Tableau de bord",
    financial_sub: "Commandes de service Odoo, factures et solde du compte.",
    logout: "Se déconnecter",
    save: "Enregistrer les modifications",
    saved: "Profil enregistré",
    name: "Nom complet",
    email: "E-mail",
    phone: "Téléphone",
    address: "Adresse",
    city: "Ville",
    country: "Pays",
    account_status: "Statut du compte",
    status_active: "Actif",
    status_inactive: "Inactif",
    status_frozen: "Gelé",
    status_deleted: "Supprimé",
    delivery_notes: "Notes de livraison",
    customer_id: "ID client",
    change_pwd: "Changer le mot de passe",
    current_pwd: "Mot de passe actuel",
    new_pwd: "Nouveau mot de passe",
    no_orders: "Vous n'avez encore passé aucune commande.",
    browse: "Parcourir BOB Store",
    order_num: "Commande #",
    date: "Date",
    status: "Statut",
    total: "Total",
    items: "Articles",
    whatsapp_help: "Besoin d'aide ? Écrivez-nous sur WhatsApp",
    legal_support: "Légal et support",
    terms: "Conditions",
    privacy: "Confidentialité",
    prohibited: "Articles interdits",
    loading: "Chargement...",
    profile_section: "Détails du profil",
    security_section: "Mot de passe et accès",
    support_note: "Gardez vos informations de livraison à jour pour faciliter les commandes et le support.",
    orders_note: "Consultez l'historique, les statuts et les totaux depuis un seul espace.",
    orders_empty_note: "Vos futures commandes apparaîtront ici avec leur statut et leur total.",
  },
};

function useT() {
  const { lang } = useLanguage();
  return STR[lang] || STR.en;
}

function RequireCustomer({ children }) {
  const { customer, loading } = useCustomerAuth();
  const t = useT();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f7f9] px-4 pt-24 pb-16">
        <div className="mx-auto flex max-w-xl items-center justify-center">
          <div className="w-full rounded-[28px] border border-black/6 bg-white px-6 py-10 text-center shadow-[0_20px_50px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#F37021]/20 border-t-[#F37021]" />
            <p className="mt-4 text-sm font-medium text-[#556070] dark:text-[#BAC4D4]">{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) return <Navigate to="/login" replace />;
  return children;
}

function Field({ label, ...props }) {
  return (
    <div className="space-y-2">
      <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5b6473] dark:text-[#BAC4D4]">{label}</label>
      <input
        {...props}
        className="h-12 w-full rounded-2xl border border-[#d9dee7] bg-[#fbfcfe] px-4 text-sm text-[#0a1628] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 disabled:bg-[#f3f5f8] disabled:text-[#7f8794] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:shadow-none dark:disabled:bg-[#1B1B1B] dark:disabled:text-[#8FA1B8]"
      />
    </div>
  );
}

function SectionCard({ icon: Icon, title, note, children, actions }) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-black/6 bg-white shadow-[0_22px_60px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]">
      <div className="border-b border-black/6 bg-[linear-gradient(180deg,rgba(243,112,33,0.08)_0%,rgba(243,112,33,0)_100%)] px-6 py-5 sm:px-7 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(243,112,33,0.12)_0%,rgba(243,112,33,0)_100%)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            {Icon && (
              <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F37021]/10 text-[#F37021]">
                <Icon className="h-5 w-5" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-[#0a1628] dark:text-white">{title}</h2>
              {note && <p className="mt-1 text-sm leading-6 text-[#697385] dark:text-[#BAC4D4]">{note}</p>}
            </div>
          </div>
          {actions}
        </div>
      </div>
      <div className="p-6 sm:p-7">{children}</div>
    </section>
  );
}

function QuickLink({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-[#fbfcfe] px-4 text-sm font-medium text-[#3f4a59] transition-all hover:-translate-y-0.5 hover:border-[#F37021]/30 hover:text-[#F37021] focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2]"
    >
      {children}
      <ExternalLink className="h-3.5 w-3.5" />
    </Link>
  );
}

function getDisplayCustomerId(customer) {
  const candidates = [customer?.customer_id, customer?.customerId, customer?.id]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const proper = candidates.find((value) => /^CUST-\d{6,7}$/i.test(value));
  if (proper) return proper.toUpperCase();
  return candidates.find((value) => !/^cust-/i.test(value)) || "";
}

function statusLabel(status, t) {
  const key = String(status || "active").toLowerCase();
  return t[`status_${key}`] || key.replace(/_/g, " ");
}

export function AccountPage() {
  const t = useT();
  const navigate = useNavigate();
  const { customer, apiFetch, setSession, logout, token, formatApiDetail } = useCustomerAuth();
  const [form, setForm] = useState({ full_name: "", phone: "", address: "", city: "", country: "", delivery_notes: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const displayCustomerId = getDisplayCustomerId(customer);
  const accountStatus = String(customer?.status || "active").toLowerCase();

  useEffect(() => {
    if (customer) {
      setForm({
        full_name: customer.full_name || "",
        phone: customer.phone || "",
        address: customer.address || "",
        city: customer.city || "",
        country: customer.country || "",
        delivery_notes: customer.delivery_notes || "",
      });
    }
  }, [customer]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch("/me", { method: "PUT", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiDetail(data?.detail));
      setSession(token, data);
      toast.success(t.saved);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onChangePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error("Min 6 characters");
    try {
      const res = await apiFetch("/change-password", { method: "POST", body: JSON.stringify(pwd) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiDetail(data?.detail));
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <RequireCustomer>
      <Navbar />
      <main className="min-h-screen bg-[#f7f7f9] pt-24 pb-16 dark:bg-[#080808]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6" data-testid="account-page">
          <section className="overflow-hidden rounded-[32px] border border-black/6 bg-[radial-gradient(circle_at_top_left,rgba(243,112,33,0.18),transparent_28%),linear-gradient(135deg,#ffffff_0%,#fbfcfe_100%)] px-6 py-7 shadow-[0_24px_70px_rgba(16,24,40,0.08)] sm:px-8 dark:border-white/10 dark:bg-[radial-gradient(circle_at_top_left,rgba(243,112,33,0.14),transparent_24%),linear-gradient(135deg,#101010_0%,#181818_100%)]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="inline-flex items-center rounded-full border border-[#F37021]/15 bg-[#F37021]/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C55B14]">
                  BOB Services LB
                </div>
                <h1 className="mt-4 text-3xl font-bold text-[#0a1628] sm:text-4xl dark:text-white">{t.account_title}</h1>
                <p className="mt-3 text-sm leading-6 text-[#667083] dark:text-[#BAC4D4]">{t.support_note}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {displayCustomerId && (
                    <div className="inline-flex items-center gap-2 rounded-full border border-black/8 bg-white/80 px-4 py-2 text-xs text-[#667083] dark:border-white/10 dark:bg-white/10 dark:text-[#BAC4D4]">
                      <span className="font-semibold text-[#0a1628] dark:text-white">{t.customer_id}</span>
                      <code className="font-mono text-[#F37021]">{displayCustomerId}</code>
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {t.account_status}: {statusLabel(accountStatus, t)}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/orders"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-medium text-[#3d4756] transition-all hover:-translate-y-0.5 hover:border-[#F37021]/30 hover:text-[#F37021] focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2]"
                >
                  <Package className="h-4 w-4" />
                  {t.orders_title}
                </Link>
                <Link
                  to="/financial"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-medium text-[#3d4756] transition-all hover:-translate-y-0.5 hover:border-[#F37021]/30 hover:text-[#F37021] focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2]"
                  data-testid="account-financial-link"
                >
                  <Briefcase className="h-4 w-4" />
                  {t.financial_title}
                </Link>
                <button
                  onClick={() => { logout(); navigate("/"); }}
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-[#111111] px-5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-black focus:outline-none focus:ring-4 focus:ring-black/10"
                  data-testid="account-logout"
                >
                  <LogOut className="h-4 w-4" />
                  {t.logout}
                </button>
              </div>
            </div>
          </section>

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.95fr)]">
            <form onSubmit={onSave} className="space-y-6">
              <SectionCard
                icon={User}
                title={t.profile_section}
                note={t.support_note}
                actions={
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex h-11 items-center justify-center rounded-full bg-[#F37021] px-5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(243,112,33,0.24)] transition-all hover:-translate-y-0.5 hover:bg-[#d85f15] hover:shadow-[0_18px_34px_rgba(243,112,33,0.28)] focus:outline-none focus:ring-4 focus:ring-[#F37021]/20 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="account-save"
                  >
                    {saving ? "..." : t.save}
                  </button>
                }
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t.name} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="account-name" />
                  <Field label={t.email} value={customer?.email || ""} disabled data-testid="account-email" />
                  <Field label={t.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone" />
                  <Field label={t.city} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="account-city" />
                  <Field label={t.country} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="account-country" />
                  <Field label={t.account_status} value={statusLabel(accountStatus, t)} disabled data-testid="account-status" />
                  <div className="md:col-span-2">
                    <Field label={t.address} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="account-address" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5b6473] dark:text-[#BAC4D4]">{t.delivery_notes}</label>
                    <textarea
                      rows={4}
                      value={form.delivery_notes}
                      onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })}
                      className="w-full rounded-2xl border border-[#d9dee7] bg-[#fbfcfe] px-4 py-3 text-sm text-[#0a1628] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-white dark:shadow-none"
                      data-testid="account-notes"
                    />
                  </div>
                </div>
              </SectionCard>
            </form>

            <div className="space-y-6">
              <form onSubmit={onChangePwd}>
                <SectionCard icon={Mail} title={t.security_section} note={t.orders_note}>
                  <div className="space-y-4">
                    <Field label={t.current_pwd} type="password" autoComplete="current-password" required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="account-current-pwd" />
                    <Field label={t.new_pwd} type="password" autoComplete="new-password" required value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="account-new-pwd" />
                    <div className="flex justify-end pt-1">
                      <button
                        type="submit"
                        className="inline-flex h-11 items-center justify-center rounded-full bg-[#111111] px-5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-black focus:outline-none focus:ring-4 focus:ring-black/10"
                        data-testid="account-change-pwd"
                      >
                        {t.change_pwd}
                      </button>
                    </div>
                  </div>
                </SectionCard>
              </form>

              <SectionCard icon={Package} title={t.legal_support} note={t.orders_note}>
                <div className="flex flex-wrap gap-3" data-testid="account-legal-links">
                  <QuickLink to="/orders">{t.orders_title}</QuickLink>
                  <QuickLink to="/terms">{t.terms}</QuickLink>
                  <QuickLink to="/privacy">{t.privacy}</QuickLink>
                  <QuickLink to="/prohibited-items">{t.prohibited}</QuickLink>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </RequireCustomer>
  );
}

export function OrdersPage() {
  const t = useT();
  const { apiFetch } = useCustomerAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/orders");
        const data = await res.json();
        if (!cancelled) setOrders(Array.isArray(data?.orders) ? data.orders : []);
      } catch {
        if (!cancelled) setOrders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  const whatsapp = "https://wa.me/96178866746";

  return (
    <RequireCustomer>
      <Navbar />
      <main className="min-h-screen bg-[#f7f7f9] pt-24 pb-16 dark:bg-[#080808]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6" data-testid="orders-page">
          <section className="overflow-hidden rounded-[32px] border border-black/6 bg-[radial-gradient(circle_at_top_left,rgba(243,112,33,0.18),transparent_28%),linear-gradient(135deg,#ffffff_0%,#fbfcfe_100%)] px-6 py-7 shadow-[0_24px_70px_rgba(16,24,40,0.08)] sm:px-8 dark:border-white/10 dark:bg-[radial-gradient(circle_at_top_left,rgba(243,112,33,0.14),transparent_24%),linear-gradient(135deg,#101010_0%,#181818_100%)]">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="inline-flex items-center rounded-full border border-[#F37021]/15 bg-[#F37021]/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C55B14]">
                  BOB Services LB
                </div>
                <h1 className="mt-4 text-3xl font-bold text-[#0a1628] sm:text-4xl dark:text-white">{t.orders_title}</h1>
                <p className="mt-3 text-sm leading-6 text-[#667083] dark:text-[#BAC4D4]">{t.orders_note}</p>
              </div>
              <Link
                to="/account"
                className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-medium text-[#3d4756] transition-all hover:-translate-y-0.5 hover:border-[#F37021]/30 hover:text-[#F37021] focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2]"
              >
                {t.account_title}
              </Link>
            </div>
          </section>

          <div className="mt-6 space-y-4">
            {orders === null ? (
              <div className="rounded-[28px] border border-black/6 bg-white p-6 shadow-[0_20px_50px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]">
                <div className="grid gap-4">
                  {[0, 1, 2].map((idx) => (
                    <div key={idx} className="animate-pulse rounded-3xl border border-black/6 bg-[#fbfcfe] p-5 dark:border-white/10 dark:bg-[#1B1B1B]">
                      <div className="h-4 w-36 rounded-full bg-black/8 dark:bg-white/12" />
                      <div className="mt-3 h-3 w-24 rounded-full bg-black/6 dark:bg-white/10" />
                      <div className="mt-4 h-3 w-full rounded-full bg-black/6 dark:bg-white/10" />
                      <div className="mt-2 h-3 w-2/3 rounded-full bg-black/6 dark:bg-white/10" />
                    </div>
                  ))}
                </div>
              </div>
            ) : orders.length === 0 ? (
              <div className="rounded-[28px] border border-black/6 bg-white px-6 py-10 text-center shadow-[0_20px_50px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]" data-testid="orders-empty">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F37021]/10 text-[#F37021]">
                  <Package className="h-7 w-7" />
                </div>
                <h2 className="text-xl font-semibold text-[#0a1628] dark:text-white">{t.no_orders}</h2>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#687283] dark:text-[#BAC4D4]">{t.orders_empty_note}</p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/stock"
                    className="inline-flex h-11 items-center gap-2 rounded-full bg-[#F37021] px-5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(243,112,33,0.24)] transition-all hover:-translate-y-0.5 hover:bg-[#d85f15] hover:shadow-[0_18px_34px_rgba(243,112,33,0.28)]"
                  >
                    {t.browse}
                  </Link>
                  <a
                    href={whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-11 items-center gap-2 rounded-full bg-green-500 px-5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-green-600"
                  >
                    <MessageSquare className="h-4 w-4" />
                    {t.whatsapp_help}
                  </a>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <article
                    key={o.order_number || o.id}
                    className="overflow-hidden rounded-[28px] border border-black/6 bg-white shadow-[0_20px_50px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212]"
                    data-testid={`order-row-${o.order_number}`}
                  >
                    <div className="border-b border-black/6 bg-[linear-gradient(180deg,rgba(243,112,33,0.08)_0%,rgba(243,112,33,0)_100%)] px-5 py-4 sm:px-6 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(243,112,33,0.12)_0%,rgba(243,112,33,0)_100%)]">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-[#0a1628] dark:text-white">
                            {t.order_num} {o.order_number || "?"}
                          </div>
                          <div className="mt-1 text-xs text-[#7a8392] dark:text-[#BAC4D4]">
                            {t.date}: {(o.created_at || "").slice(0, 10)}
                          </div>
                        </div>
                        <span className="inline-flex h-8 items-center rounded-full bg-[#0a1628]/10 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#0a1628] dark:bg-white/10 dark:text-white">
                          {o.status || "pending"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                      {Array.isArray(o.items) && o.items.length > 0 && (
                        <div className="rounded-2xl border border-black/6 bg-[#fbfcfe] px-4 py-3 text-sm leading-6 text-[#5c6676] dark:border-white/10 dark:bg-[#1B1B1B] dark:text-[#C9D4E3]">
                          <span className="font-semibold text-[#0a1628] dark:text-white">{t.items}:</span>{" "}
                          {o.items.map((it) => `${it.name || it.title || "Item"} x${it.quantity || 1}`).join(", ")}
                        </div>
                      )}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-base font-bold text-[#F37021]">
                          {t.total}: ${Number(o.total_usd || o.total || 0).toFixed(2)}
                        </div>
                        <a
                          href={whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-10 items-center gap-2 self-start rounded-full border border-green-200 bg-green-50 px-4 text-sm font-medium text-green-700 transition-all hover:-translate-y-0.5 hover:bg-green-100 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-300 dark:hover:bg-green-500/18"
                        >
                          <MessageSquare className="h-4 w-4" />
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-3 text-sm text-[#556070] transition-all hover:-translate-y-0.5 hover:border-[#F37021]/30 hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2] dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t.whatsapp_help}
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </RequireCustomer>
  );
}
